import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Persian translations (bundled — always available, default language)
import commonFA from './locales/fa/common.json';
import authFA from './locales/fa/auth.json';
import settingsFA from './locales/fa/settings.json';
import feedFA from './locales/fa/feed.json';
import chatFA from './locales/fa/chat.json';
import profileFA from './locales/fa/profile.json';
import notificationsFA from './locales/fa/notifications.json';
import modalsFA from './locales/fa/modals.json';
import adminFA from './locales/fa/admin.json';
import livestreamFA from './locales/fa/livestream.json';
import shopFA from './locales/fa/shop.json';
import legalFA from './locales/fa/legal.json';

export const SUPPORTED_LANGUAGES = [
  { code: 'fa', name: 'Persian', nativeName: 'فارسی', dir: 'rtl', flag: '🇮🇷' },
  { code: 'en', name: 'English', nativeName: 'English', dir: 'ltr', flag: '🇬🇧' },
];

export const NAMESPACES = [
  'common', 'auth', 'settings', 'feed', 'chat',
  'profile', 'notifications', 'modals', 'admin', 'livestream', 'shop', 'legal',
];

// Lazy-load non-Persian translations
const loadLanguageBundle = async (lang) => {
  const modules = await Promise.all([
    import(`./locales/${lang}/common.json`),
    import(`./locales/${lang}/auth.json`),
    import(`./locales/${lang}/settings.json`),
    import(`./locales/${lang}/feed.json`),
    import(`./locales/${lang}/chat.json`),
    import(`./locales/${lang}/profile.json`),
    import(`./locales/${lang}/notifications.json`),
    import(`./locales/${lang}/modals.json`),
    import(`./locales/${lang}/admin.json`),
    import(`./locales/${lang}/livestream.json`),
    import(`./locales/${lang}/shop.json`),
    import(`./locales/${lang}/legal.json`),
  ]);

  const namespaces = NAMESPACES;
  const bundle = {};
  modules.forEach((mod, idx) => {
    bundle[namespaces[idx]] = mod.default || mod;
  });
  return bundle;
};

// Detect initial language: localStorage > 'fa' (default, regardless of browser locale)
const detectLanguage = () => {
  const stored = localStorage.getItem('i18nextLng');
  if (stored && SUPPORTED_LANGUAGES.some(l => l.code === stored)) return stored;

  return 'fa';
};

const initPromise = i18n
  .use(initReactI18next)
  .init({
    resources: {
      fa: {
        common: commonFA,
        auth: authFA,
        settings: settingsFA,
        feed: feedFA,
        chat: chatFA,
        profile: profileFA,
        notifications: notificationsFA,
        modals: modalsFA,
        admin: adminFA,
        livestream: livestreamFA,
        shop: shopFA,
        legal: legalFA,
      },
    },
    lng: detectLanguage(),
    fallbackLng: 'fa',
    defaultNS: 'common',
    ns: NAMESPACES,
    interpolation: {
      escapeValue: false, // React already escapes
    },
    react: {
      useSuspense: false, // Safe for incremental migration
    },
    // Dev mode: warn on missing keys
    saveMissing: process.env.NODE_ENV === 'development',
    missingKeyHandler: process.env.NODE_ENV === 'development'
      ? (lngs, ns, key) => {
          console.warn(`[i18n] Missing key: ${ns}:${key} for languages: ${lngs.join(', ')}`);
        }
      : false,
  });

// Resolves once the detected/persisted language is actually renderable.
// `lng` above is read synchronously from localStorage/navigator, but only
// Persian resources are bundled at module load — awaiting this before the
// app's first render (see src/index.js) means a session with e.g. `en`
// persisted never paints a Persian frame that then flickers to English;
// it just renders once, correctly, once the bundle is in.
export const i18nReady = initPromise.then(async () => {
  const initialLang = detectLanguage();
  if (initialLang !== 'fa') {
    await changeLanguage(initialLang);
  }
});

// Change language with lazy loading
export const changeLanguage = async (lang) => {
  if (lang === 'fa') {
    await i18n.changeLanguage('fa');
    localStorage.setItem('i18nextLng', 'fa');
    return;
  }

  // Check if already loaded
  if (i18n.hasResourceBundle(lang, 'common')) {
    await i18n.changeLanguage(lang);
    localStorage.setItem('i18nextLng', lang);
    return;
  }

  // Lazy load the bundle
  try {
    const bundle = await loadLanguageBundle(lang);
    Object.entries(bundle).forEach(([ns, resources]) => {
      i18n.addResourceBundle(lang, ns, resources, true, true);
    });
    await i18n.changeLanguage(lang);
    localStorage.setItem('i18nextLng', lang);
  } catch (error) {
    console.error(`[i18n] Failed to load language: ${lang}`, error);
    // Fallback to Persian
    await i18n.changeLanguage('fa');
    localStorage.setItem('i18nextLng', 'fa');
  }
};

export default i18n;
